import { useT } from "@transifex/react";
import { getVolumeClassesLabel, type CatalogBook } from "../../../helpers/BookCatalog";
import type { BookCatalogVolume } from "../../../networking/bookCatalog";
import BookSelectionStepHeading from "./BookSelectionStepHeading";
import SelectionCard from "./SelectionCard";

const COVER_CLASSES = "aspect-3/4 w-xxxxxl shrink-0 rounded-xsmall-container ring-1 ring-transparent-black";

interface VolumeStepProps {
    book: CatalogBook;
    volume?: BookCatalogVolume;
    onPick: (volume: BookCatalogVolume) => void;
}

const VolumeStep = (props: VolumeStepProps) => {
    const t = useT();
    const t_title = t("Which volume are you using?", { _tags: "CreateTest,SelectBook" });
    const t_comingSoon = t("Coming soon", { _tags: "CreateTest,SelectBook" });

    // Wide cards with the cover on the left, so long volume names (often the whole title of a single volume) have room.
    const renderVolumeCard = (volume: BookCatalogVolume) => {
        const classes = getVolumeClassesLabel(volume);
        // Most volumes have a cover of their own; the book's stands in for the rest.
        const coverImage = volume.imageUrl || props.book.coverImage;

        return (
            <SelectionCard
                key={volume.articleId}
                isSelected={volume.articleId === props.volume?.articleId}
                isDisabled={volume.isComingSoon}
                onClick={() => props.onPick(volume)}
                aria-label={volume.name}
                data-testid="book-selection-volume-card"
            >
                {coverImage ? (
                    <img
                        src={coverImage}
                        alt=""
                        loading="lazy"
                        className={`${COVER_CLASSES} object-cover`}
                    />
                ) : (
                    <span
                        aria-hidden="true"
                        className={`${COVER_CLASSES} bg-background-light`}
                    />
                )}
                <span className="flex min-w-no flex-col gap-xxs">
                    <span className="text-l font-medium text-p6-grey-dark">{volume.name}</span>
                    {classes && <span className="text-m text-p6-grey-mid">{classes}</span>}
                    {/* One per line: a few volumes have up to eleven, which run together on a single line. */}
                    {volume.isbns.length > 0 && (
                        <span
                            className="flex gap-xxs text-s text-p6-grey-mid"
                            data-testid="book-selection-volume-isbn"
                        >
                            <span>ISBN</span>
                            <span className="flex flex-col tabular-nums">
                                {volume.isbns.map((isbn) => (
                                    <span
                                        key={isbn}
                                        className="whitespace-nowrap"
                                    >
                                        {isbn}
                                    </span>
                                ))}
                            </span>
                        </span>
                    )}
                    {volume.isComingSoon && (
                        <span className="self-start rounded-pill bg-background-light px-xs text-s font-medium text-p6-grey-dark">
                            {t_comingSoon}
                        </span>
                    )}
                </span>
            </SelectionCard>
        );
    };

    return (
        <div className="flex flex-col gap-m">
            <BookSelectionStepHeading title={t_title} />
            <div className="grid grid-cols-[repeat(auto-fill,minmax(min(var(--size-volume-card),100%),1fr))] gap-s">
                {props.book.volumes.map(renderVolumeCard)}
            </div>
        </div>
    );
};

export default VolumeStep;
